/**
 * The badge legend — an info icon that opens a popover explaining what each badge on the
 * card means.
 *
 * Deliberately tap-to-open rather than hover: a stylus and a finger cannot hover, and mobile
 * is the primary way EFSS gets used. Opens and closes the same way `SplitButton`'s menu
 * does — outside pointerdown or Escape — so the two popovers in the app behave identically.
 */

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Info } from "./icons";

/**
 * `preview` is the actual badge markup — same classes, same drawn `icons.tsx` glyphs, same
 * example text a row would show — not a re-description of it. A separate emoji-and-words
 * legend drifted from what the badges actually look like the moment either side changed;
 * reusing the real markup can't drift, because it *is* the thing being explained.
 *
 * `title` is the plain-word name of the badge, always shown — legend and glossary in one
 * line: the word on the left, the badge itself on the right, so a reader can match either
 * direction (name the badge, or point at it and find its name).
 */
export type GlossaryEntry = { key: string; title: string; preview: ReactNode; description: string };

export function BadgeGlossary({
  entries,
  title = "What the badges mean",
}: {
  entries: GlossaryEntry[];
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="glossary" ref={rootRef}>
      <button
        type="button"
        className="btn ghost small glossary-toggle"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={title}
        title={title}
      >
        <Info />
      </button>
      {open && (
        <div className="glossary-popover" role="dialog" aria-label={title}>
          <dl>
            {entries.map((entry) => (
              <div className="glossary-entry" key={entry.key}>
                <dt>
                  <span className="glossary-title">{entry.title}</span>
                  {entry.preview}
                </dt>
                <dd>{entry.description}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
